import { FC, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Breadcrumb,
  Card,
  Label,
  Select,
  Spinner,
  Table,
  TextInput,
  useThemeMode,
} from "flowbite-react";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { HiCheckCircle, HiExclamationCircle, HiHome } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import TablePagination from "@/components/Pagination";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";
import { useGetAllCompaniesQuery } from "@/api/companyApi/company";
import { useSelectedCompany } from "@/context/SelectedCompanyContext";
import {
  useGetTtsUsageLogsQuery,
  useGetTtsUsageSummaryQuery,
  type TtsUsageBucket,
  type TtsUsageQuery,
} from "@/api/ttsApi/tts";

const PAGE_SIZE = 50;

const fmtUsd = (n: number | null | undefined): string =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;

const fmtInt = (n: number | null | undefined): string =>
  (Number(n) || 0).toLocaleString();

/** YYYY-MM-DD of a local date — what the date inputs and the gateway expect. */
const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Longest range drawn day by day; past it only days with requests get a bar. */
const MAX_FILLED_DAYS = 92;

/**
 * Every day of the range, with zeros where nothing was sent — the gateway only
 * returns days that had requests, and a chart that skips the quiet days hides
 * them. Days after today are left off.
 */
const fillDays = (
  days: (TtsUsageBucket & { date: string })[],
  start: string,
  end: string,
): (TtsUsageBucket & { date: string })[] => {
  if (!start || !end) return days;
  const byDate = new Map(days.map((d) => [d.date, d]));
  const last = [end, isoDay(new Date())].sort()[0];
  const out: (TtsUsageBucket & { date: string })[] = [];
  const [y, m, d] = start.split("-").map(Number);
  for (let day = new Date(y, m - 1, d); isoDay(day) <= last; day.setDate(day.getDate() + 1)) {
    if (out.length >= MAX_FILLED_DAYS) return days;
    const key = isoDay(day);
    out.push(byDate.get(key) ?? { date: key, requests: 0, failed_requests: 0, chars: 0, cost_usd: 0 });
  }
  return out;
};

/** "2026-09-14" → "14.09" for the chart axis. */
const shortDay = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

const StatCard: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Card className="min-w-[160px] flex-1">
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
  </Card>
);

const BreakdownTable: FC<{
  heading: string;
  nameHeader: string;
  rows: (TtsUsageBucket & { key: string; name: string })[];
}> = ({ heading, nameHeader, rows }) => {
  const { t } = useTranslation(["ttsUsage"]);
  return (
    <div className="min-w-0 flex-1">
      <h3 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">{heading}</h3>
      <div className="overflow-x-auto">
        <Table striped>
          <Table.Head>
            <Table.HeadCell>{nameHeader}</Table.HeadCell>
            <Table.HeadCell>{t("table.requests")}</Table.HeadCell>
            <Table.HeadCell>{t("table.failed")}</Table.HeadCell>
            <Table.HeadCell>{t("table.chars")}</Table.HeadCell>
            <Table.HeadCell>{t("table.cost")}</Table.HeadCell>
          </Table.Head>
          <Table.Body className="divide-y">
            {rows.map((r) => (
              <Table.Row key={r.key} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                <Table.Cell className="font-medium text-gray-900 dark:text-white">{r.name}</Table.Cell>
                <Table.Cell>{fmtInt(r.requests)}</Table.Cell>
                <Table.Cell>{fmtInt(r.failed_requests)}</Table.Cell>
                <Table.Cell>{fmtInt(r.chars)}</Table.Cell>
                <Table.Cell>{fmtUsd(r.cost_usd)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};

/**
 * Super admin: what was sent to Google Text-to-Speech and what it cost,
 * across every company. The gateway logs each synthesis it proxies.
 */
const TtsUsagePage: FC = () => {
  const { t } = useTranslation(["ttsUsage"]);
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const user = useCurrentUser();
  const isSuperAdmin = user?.agent_role === agentRoles.super_admin;
  const { selectedCompany } = useSelectedCompany();

  const today = new Date();
  const [startDate, setStartDate] = useState(() =>
    isoDay(new Date(today.getFullYear(), today.getMonth(), 1)),
  );
  const [endDate, setEndDate] = useState(() => isoDay(today));
  // "" = all companies, "none" = super admins, otherwise a company id.
  const [company, setCompany] = useState<string>(() =>
    selectedCompany ? String(selectedCompany.id) : "",
  );
  const [status, setStatus] = useState<"" | "success" | "error">("");
  const [page, setPage] = useState(1);

  const invalidRange = !!startDate && !!endDate && startDate > endDate;

  const filters: TtsUsageQuery = {
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    company_id: company === "" ? undefined : company === "none" ? "none" : Number(company),
  };

  useEffect(() => setPage(1), [startDate, endDate, company, status]);

  const skip = !isSuperAdmin || invalidRange;
  const { data: companies } = useGetAllCompaniesQuery({ page: 1, pageSize: 200 }, { skip: !isSuperAdmin });
  const { data: summary, isFetching: summaryLoading } = useGetTtsUsageSummaryQuery(filters, { skip });
  const { data: logs, isFetching: logsLoading } = useGetTtsUsageLogsQuery(
    { ...filters, status: status || undefined, page, pageSize: PAGE_SIZE },
    { skip },
  );

  const tierLabel = (tier: string) => t(`tiers.${tier}`, { defaultValue: tier });
  const companyLabel = (id: number | null, name: string | null) =>
    id === null ? t("table.superAdmin") : name ?? `#${id}`;

  const byDay = useMemo(
    () => fillDays(summary?.by_day ?? [], startDate, endDate),
    [summary, startDate, endDate],
  );
  const chartOptions = useMemo<ApexOptions>(() => {
    const ink = isDark ? "#9CA3AF" : "#6B7280";
    return {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "Inter, sans-serif", background: "transparent" },
      plotOptions: {
        bar: { columnWidth: "60%", borderRadius: 4, borderRadiusApplication: "end" },
      },
      dataLabels: { enabled: false },
      colors: [isDark ? "#3F83F8" : "#1A56DB"],
      grid: { borderColor: isDark ? "#374151" : "#E5E7EB", strokeDashArray: 4, xaxis: { lines: { show: false } } },
      xaxis: {
        categories: byDay.map((d) => shortDay(d.date)),
        labels: { style: { colors: ink }, rotate: -45, hideOverlappingLabels: true },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: ink }, formatter: (v) => fmtUsd(v) } },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: {
          formatter: (v, { dataPointIndex }) => {
            const d = byDay[dataPointIndex];
            return d
              ? `${fmtUsd(v)} · ${t("tooltip.requests", { count: d.requests })} · ${t("tooltip.chars", { count: d.chars })}`
              : fmtUsd(v);
          },
        },
      },
      states: { hover: { filter: { type: "darken" } } },
      theme: { mode: isDark ? "dark" : "light" },
    };
  }, [byDay, isDark, t]);

  if (!isSuperAdmin) return <Navigate to="/" replace />;

  const totals = summary?.totals;

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6">
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item href="/">
            <div className="flex items-center gap-x-3">
              <HiHome className="text-xl" />
              <span className="dark:text-white">{t("home")}</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{t("title")}</Breadcrumb.Item>
        </Breadcrumb>

        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t("title")}</h1>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">{t("subtitle")}</p>

        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="tts-usage-start" value={t("filters.startDate")} />
            <TextInput id="tts-usage-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="tts-usage-end" value={t("filters.endDate")} />
            <TextInput id="tts-usage-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="min-w-[220px]">
            <Label htmlFor="tts-usage-company" value={t("filters.company")} />
            <Select id="tts-usage-company" value={company} onChange={(e) => setCompany(e.target.value)}>
              <option value="">{t("filters.allCompanies")}</option>
              <option value="none">{t("filters.superAdmins")}</option>
              {(companies?.items ?? []).map((c) => (
                <option key={c.company_id} value={String(c.company_id)}>
                  {c.company_name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {invalidRange ? (
          <p className="text-sm text-red-600 dark:text-red-400">{t("invalidRange")}</p>
        ) : summaryLoading && !summary ? (
          <Spinner />
        ) : (
          <div className="flex flex-col gap-6 pb-8">
            <div>
              <div className="flex flex-wrap gap-4">
                <StatCard label={t("stats.cost")} value={fmtUsd(totals?.cost_usd)} />
                <StatCard label={t("stats.requests")} value={fmtInt(totals?.requests)} />
                <StatCard label={t("stats.chars")} value={fmtInt(totals?.chars)} />
                <StatCard label={t("stats.failed")} value={fmtInt(totals?.failed_requests)} />
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t("listPriceNote")}</p>
              {!!totals?.unpriced_chars && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("unpricedNote", { chars: fmtInt(totals.unpriced_chars) })}
                </p>
              )}
            </div>

            {!totals?.requests ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t("noData")}</p>
            ) : (
              <>
                <Card>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t("dailyCost")}</h3>
                  <ReactApexChart
                    options={chartOptions}
                    series={[{ name: t("stats.cost"), data: byDay.map((d) => d.cost_usd) }]}
                    type="bar"
                    height={280}
                  />
                </Card>

                <div className="flex flex-col gap-6 xl:flex-row">
                  <BreakdownTable
                    heading={t("byCompany")}
                    nameHeader={t("table.company")}
                    rows={(summary?.by_company ?? []).map((r) => ({
                      ...r,
                      key: String(r.company_id),
                      name: companyLabel(r.company_id, r.company_name),
                    }))}
                  />
                  <BreakdownTable
                    heading={t("byTier")}
                    nameHeader={t("table.tier")}
                    rows={(summary?.by_tier ?? []).map((r) => ({
                      ...r,
                      key: r.voice_tier,
                      name: tierLabel(r.voice_tier),
                    }))}
                  />
                </div>
              </>
            )}

            <div>
              <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t("logs")}</h3>
                <div className="min-w-[160px]">
                  <Label htmlFor="tts-usage-status" value={t("filters.status")} />
                  <Select
                    id="tts-usage-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as typeof status)}
                  >
                    <option value="">{t("filters.allStatuses")}</option>
                    <option value="success">{t("filters.success")}</option>
                    <option value="error">{t("filters.error")}</option>
                  </Select>
                </div>
              </div>

              {logsLoading && !logs ? (
                <Spinner />
              ) : !logs?.items.length ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t("noData")}</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table striped>
                      <Table.Head>
                        <Table.HeadCell>{t("table.date")}</Table.HeadCell>
                        <Table.HeadCell>{t("table.company")}</Table.HeadCell>
                        <Table.HeadCell>{t("table.agent")}</Table.HeadCell>
                        <Table.HeadCell>{t("table.voice")}</Table.HeadCell>
                        <Table.HeadCell>{t("table.tier")}</Table.HeadCell>
                        <Table.HeadCell>{t("table.format")}</Table.HeadCell>
                        <Table.HeadCell>{t("table.chars")}</Table.HeadCell>
                        <Table.HeadCell>{t("table.cost")}</Table.HeadCell>
                        <Table.HeadCell>{t("table.status")}</Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {logs.items.map((row) => (
                          <Table.Row key={row.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                            <Table.Cell className="whitespace-nowrap">
                              {new Date(row.createdAt).toLocaleString()}
                            </Table.Cell>
                            <Table.Cell>{companyLabel(row.company_id, row.company_name)}</Table.Cell>
                            <Table.Cell>{row.agent_username ?? (row.agent_id ? `#${row.agent_id}` : "-")}</Table.Cell>
                            <Table.Cell className="whitespace-nowrap">{row.voice_name}</Table.Cell>
                            <Table.Cell>{tierLabel(row.voice_tier)}</Table.Cell>
                            <Table.Cell>{row.audio_encoding === "LINEAR16" ? "WAV" : row.audio_encoding}</Table.Cell>
                            <Table.Cell>{fmtInt(row.char_count)}</Table.Cell>
                            <Table.Cell>{row.cost_usd === null ? "-" : fmtUsd(row.cost_usd)}</Table.Cell>
                            <Table.Cell>
                              {row.status === "success" ? (
                                <Badge color="success" icon={HiCheckCircle} className="w-fit">
                                  {t("filters.success")}
                                </Badge>
                              ) : (
                                <span title={row.error_message ?? undefined}>
                                  <Badge color="failure" icon={HiExclamationCircle} className="w-fit">
                                    {t("filters.error")}
                                  </Badge>
                                </span>
                              )}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                  <div className="mt-4">
                    <TablePagination pagination={logs.pagination} onPageChange={setPage} />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </NavbarSidebarLayout>
  );
};

export default TtsUsagePage;
